const STORAGE_KEY = 'lastVisitedCollection';

/**
 * Store the current collection as the last visited collection.
 * Uses the breadcrumb current-page label as the title.
 */
function storeLastVisitedCollection() {
  try {
    const templateName = /** @type {string | undefined} */ (Theme?.template?.name);
    if (!templateName || !templateName.startsWith('collection')) return;

    const currentCrumb = document.querySelector(
      '.breadcrumbs .breadcrumbs__link[aria-current="page"]'
    );

    const title = currentCrumb?.textContent?.trim();
    const url = window.location.href.split('#')[0];

    if (!title || !url) return;

    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        url,
        title,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    // Fail silently if sessionStorage is unavailable
  }
}

/**
 * Enhance product breadcrumbs:
 * - If the last page was the homepage, show ONLY "Home / Product" (remove collection crumb)
 * - If the last page was a collection, point the collection crumb back to that exact page
 *   and use the best available collection title (stored, fetched, or derived).
 * - Otherwise, keep the Liquid-rendered breadcrumb as-is.
 */
function enhanceProductBreadcrumb() {
  try {
    const templateName = /** @type {string | undefined} */ (Theme?.template?.name);
    if (!templateName || !templateName.startsWith('product')) return;

    const breadcrumbList = document.querySelector('.breadcrumbs__list');
    if (!breadcrumbList) return;

    /** @type {HTMLAnchorElement | null} */
    let collectionLink = null;
    const links = breadcrumbList.querySelectorAll('.breadcrumbs__item .breadcrumbs__link');

    for (const link of links) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      if (link.href.includes('/collections/')) {
        collectionLink = link;
        break;
      }
    }

    if (!collectionLink) return;

    const referrer = document.referrer || '';
    const origin = window.location.origin || '';

    const homePath =
      (Theme &&
        Theme.routes &&
        typeof Theme.routes.root_url === 'string' &&
        Theme.routes.root_url) ||
      '/';

    const homeUrlA = origin + homePath; // e.g. https://example.com/ or https://example.com/en
    const homeUrlB = homeUrlA.replace(/\/$/, ''); // e.g. https://example.com or https://example.com/en

    const cameFromHome =
      referrer === homeUrlA ||
      referrer === homeUrlB ||
      referrer.startsWith(homeUrlA + '?') ||
      referrer.startsWith(homeUrlB + '?') ||
      referrer.startsWith(homeUrlA + '#') ||
      referrer.startsWith(homeUrlB + '#');

    const cameFromCollection =
      !cameFromHome &&
      referrer.includes('/collections/') &&
      !referrer.includes('/products/');

    // If we came from the homepage, remove the collection crumb so we only show "Home / Product"
    if (cameFromHome) {
      const item = collectionLink.closest('.breadcrumbs__item');
      if (item && item.parentNode) {
        item.parentNode.removeChild(item);
      }
      return;
    }

    // If we didn't come from a collection page, keep the Liquid-rendered breadcrumb
    if (!cameFromCollection) {
      return;
    }

    // Default to the current Liquid-rendered label
    const fallbackTitle = collectionLink.textContent || '';

    // 1) Try to read the last visited collection title from sessionStorage
    let storedTitle = null;
    let storedFresh = false;
    try {
      const storedRaw = sessionStorage.getItem(STORAGE_KEY);
      if (storedRaw) {
        const storedData = JSON.parse(storedRaw);
        const maxAge = 60 * 60 * 1000; // 1 hour
        if (
          storedData &&
          storedData.title &&
          storedData.timestamp &&
          Date.now() - storedData.timestamp < maxAge
        ) {
          storedTitle = String(storedData.title);
          storedFresh = true;
        }
      }
    } catch {
      // ignore malformed storage
    }

    // 2) Parse the collection handle from the referrer URL so we can fall back to
    //    either a derived title or a live lookup via the /collections/<handle>.json endpoint.
    let derivedTitle = null;
    let refHandle = null;
    try {
      let refPath = referrer;
      if (origin && refPath.startsWith(origin)) {
        refPath = refPath.slice(origin.length);
      }

      const collectionsSplit = refPath.split('/collections/');
      if (collectionsSplit.length > 1) {
        const afterCollections = collectionsSplit[1];
        refHandle = decodeURIComponent(afterCollections?.split(/[/?#]/)[0] || '');
        if (refHandle) {
          const words = refHandle.split('-').map((word) =>
            word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
          );
          derivedTitle = words.join(' ');
        }
      }
    } catch {
      // ignore referrer parsing issues
    }

    // Always point back to the exact referrer URL (with filters, pagination, etc.)
    collectionLink.setAttribute('href', referrer);

    // Prefer the freshest stored title from a real collection template.
    if (storedFresh && storedTitle) {
      collectionLink.textContent = storedTitle;
      return;
    }

    // If we have a handle, try to fetch the actual collection title via Shopify's JSON endpoint.
    if (refHandle) {
      try {
        fetch(`/collections/${encodeURIComponent(refHandle)}.json`)
          .then((res) => {
            if (!res.ok) return null;
            return res.json();
          })
          .then((data) => {
            if (data && data.collection && data.collection.title) {
              collectionLink.textContent = String(data.collection.title);
            } else if (derivedTitle) {
              collectionLink.textContent = derivedTitle;
            } else {
              collectionLink.textContent = fallbackTitle;
            }
          })
          .catch(() => {
            collectionLink.textContent = derivedTitle || fallbackTitle;
          });
      } catch {
        collectionLink.textContent = derivedTitle || fallbackTitle;
      }
      return;
    }

    // Final fallback: derived title from handle or original Liquid label.
    collectionLink.textContent = derivedTitle || fallbackTitle;
  } catch {
    // Fail silently to avoid breaking the page
  }
}

function initBreadcrumbs() {
  storeLastVisitedCollection();
  enhanceProductBreadcrumb();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBreadcrumbs);
} else {
  initBreadcrumbs();
}


